import { useRef } from 'react';
import styled from 'styled-components';
import { CropImageInput, InfoIcon, Icon, P, SvgInlineWrapper, lightTheme, TooltipWrapper, Div } from '@vgrubs/components';
import { UploadedImage } from '../UploadedImage/UploadedImage';

const AdminInputStyled = styled.div<{
    $textCenter?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $textCenter }) => ($textCenter ? 'center' : 'flex-start')};
  input {
    visibility: hidden;
    height: 0;
    width: 0;
  }
`;

export const AdminImageCropInput = ({
    image,
    onChange,
    label,
    placeholder,
    onDelete,
    noChange,
    textCenter,
    shape = 'square',
    width = '100%',
    height,
    heightSM,
    tooltip,
    aspect,
    zIndex,
}: {
    image: File | Blob | string | null;
    onChange: (img: File | Blob | string | null) => void;
    label?: string;
    placeholder?: React.ElementType | string;
    onDelete?: () => void;
    noChange?: boolean;
    textCenter?: boolean;
    shape?: 'square' | 'circle';
    width?: string;
    height?: string;
    heightSM?: string;
    tooltip?: string;
    /**
     * Crop ratio. Defaults to a square for a circular target and to the PDF
     * hero banner otherwise, which is what every caller before avatars wanted.
     */
    aspect?: number;
    /** Raise when this sits inside a modal that is itself above 130. */
    zIndex?: number;
}) => {
    const inputFileRef = useRef<HTMLInputElement>(null);

    const onBtnClick = () => {
        if (noChange) return;
        inputFileRef.current?.click();
    };


    return (
        <AdminInputStyled $textCenter={textCenter}>
            <Div $flex $alignCenter $gap="4px" $mb="10">
                <P $mb="0" >
                    <b>{label}</b>
                </P>
                {tooltip && <TooltipWrapper tooltip={tooltip} >
                    <SvgInlineWrapper>
                        <Icon color={lightTheme.darkGray}>
                            <InfoIcon />
                        </Icon>
                    </SvgInlineWrapper>
                </TooltipWrapper>}
            </Div>

            <UploadedImage
                shape={shape}
                image={image}
                PlaceholderImage={placeholder as React.ElementType}
                onDelete={onDelete}
                onClick={onBtnClick}
                noChange={noChange}
                width={width}
                height={height}
                heightSM={heightSM}
            />

            {/* A round target has to crop square: cropping an avatar to the
                hero banner's 3.5:1 gave a strip with the face cut off. */}
            <CropImageInput
                onChange={onChange}
                ref={inputFileRef}
                tooltip={tooltip}
                aspect={aspect ?? (shape === 'circle' ? 1 : undefined)}
                cropShape={shape === 'circle' ? 'round' : 'rect'}
                zIndex={zIndex}
            />
        </AdminInputStyled>
    );
};
